import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Plane,
  Cpu,
  Factory,
  Mountain,
  Waves,
  Building,
  Trees,
  Sprout,
  Route,
  Download,
  FileText,
  Loader2,
  Mail,
  MessageCircle,
  X,
} from "lucide-react";
import SmartImage from "@/components/common/SmartImage";
import { fadeUp } from "@/lib/motion";
import { submitLead, fetchBrochures, triggerBrochureFileDownload } from "@/lib/api";
import ProjectMap3D from "@/components/silva/ProjectMap3D";
import DynamicImage from "@/components/common/DynamicImage";
import SEO from "@/components/common/SEO";
import { getPageSeo } from "@/lib/seo";
import { useSettings } from "@/lib/SiteContent";

const LOCATION_POINTS = [
  {
    icon: Plane,
    title: "Purandar International Airport",
    distance: "15–20 min away",
    desc: "Direct connectivity to India’s next major aviation hub.",
    accent: "gold",
  },
  {
    icon: Route,
    title: "Pune – Solapur Highway",
    distance: "5 min away",
    desc: "Effortless access to Pune’s economic and academic corridors.",
    accent: "gold",
  },
  {
    icon: Cpu,
    title: "CMC Tech — Proposed IT Parks",
    distance: "Adjoining",
    desc: "Future employment magnet for India’s technology workforce.",
    accent: "emerald",
  },
  {
    icon: Factory,
    title: "MIDC Industrial Hubs",
    distance: "Close vicinity",
    desc: "Strategic proximity to Maharashtra’s manufacturing belt.",
    accent: "gold",
  },
];

const AMENITIES = [
  {
    icon: Mountain,
    title: "9-Acre Wellness Spa Centre",
    desc: "A dedicated sanctuary for daily restoration and lifestyle wellness.",
  },
  {
    icon: Waves,
    title: "Infinity Swimming Pool",
    desc: "An architectural infinity pool that meets the horizon at dusk.",
  },
  {
    icon: Building,
    title: "Heritage Clubhouse",
    desc: "Members’ lounge, fine dining, library, and concierge.",
  },
  {
    icon: Trees,
    title: "Forest Trails",
    desc: "Curated walking paths through indigenous tree groves.",
  },
  {
    icon: Sprout,
    title: "Organic Urban Gardens",
    desc: "Community-led organic farming plots and orchard avenues.",
  },
  {
    icon: Route,
    title: "Wide Internal Roads",
    desc: "24/7 security, dedicated power back-up, gated community.",
  },
];

const formatSize = (bytes) => {
  if (!bytes) return null;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// --- "Your brochures" download list — rendered after a successful submission
const BrochureDownloadList = ({ brochures, onDownload, downloadingId }) => (
  <div
    data-testid="brochure-download-list"
    className="mt-6 rounded-xl border border-[color:rgba(212,175,55,0.35)] bg-[color:var(--sp-gold-soft)] p-5 sm:p-6"
  >
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <p className="text-[11px] tracking-[0.28em] uppercase text-[color:var(--sp-gold)]">
        <span className="gold-divider" /> Your Brochures ({brochures.length})
      </p>
      <p className="text-xs text-white/55">Click each to download.</p>
    </div>
    <ul className="mt-4 space-y-2.5">
      {brochures.map((b) => (
        <li
          key={b.id}
          data-testid={`brochure-list-item-${b.id}`}
          className="group flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-4 hover:border-[color:rgba(212,175,55,0.5)] transition-colors"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[color:rgba(212,175,55,0.15)] text-[color:var(--sp-gold)] border border-[color:rgba(212,175,55,0.3)]">
            <FileText className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate" data-testid={`brochure-list-title-${b.id}`}>
              {b.title}
            </p>
            {b.description && (
              <p className="text-xs text-white/60 mt-0.5 leading-snug">{b.description}</p>
            )}
            {formatSize(b.sizeBytes) && (
              <p className="text-[10px] tracking-wider text-white/35 mt-1">PDF · {formatSize(b.sizeBytes)}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onDownload(b)}
            disabled={downloadingId === b.id}
            data-testid={`brochure-list-download-${b.id}`}
            className="btn-gold inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase whitespace-nowrap disabled:opacity-60"
          >
            {downloadingId === b.id ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Downloading
              </>
            ) : (
              <>
                <Download className="h-3.5 w-3.5" /> Download
              </>
            )}
          </button>
        </li>
      ))}
    </ul>
  </div>
);

// --- "We'll send it to you" fallback modal when no brochures uploaded yet
const BrochureUnavailableDialog = ({ open, onClose, title, message, whatsappNumber, whatsappDigits, email }) => {
  if (!open) return null;
  const waMessage = encodeURIComponent(
    "Hi Seetai Properties team, I just submitted the brochure form. Please share the Silva City brochure with me on WhatsApp. Thank you."
  );
  // Prefer explicit digits when available; otherwise sanitize the visible number.
  const cleaned = (whatsappDigits || (whatsappNumber || "").replace(/\D/g, "")).replace(/^0+/, "");
  const whatsappHref = cleaned ? `https://wa.me/${cleaned}?text=${waMessage}` : null;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm px-3 sm:px-4 py-6"
          onClick={onClose}
          data-testid="brochure-unavailable-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="brochure-unavailable-title"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="glass-strong rounded-2xl border border-[color:rgba(212,175,55,0.3)] p-5 sm:p-7 md:p-8 max-w-md w-full relative max-h-[92vh] overflow-y-auto"
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="brochure-unavailable-close"
              className="absolute top-3 right-3 sm:top-4 sm:right-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white/65 transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[color:rgba(212,175,55,0.15)] text-[color:var(--sp-gold)] border border-[color:rgba(212,175,55,0.32)]">
              <Mail className="h-5 w-5" />
            </div>
            <p className="mt-5 text-[10px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> Brochure delivery
            </p>
            <h3
              id="brochure-unavailable-title"
              data-testid="brochure-unavailable-title"
              className="font-display text-2xl md:text-3xl mt-2 leading-snug pr-8"
            >
              {title}
            </h3>
            <p
              data-testid="brochure-unavailable-text"
              className="mt-3 text-sm text-white/70 leading-relaxed whitespace-pre-line"
            >
              {message}
            </p>

            {/* Action row.
                Layout strategy:
                  * <=sm  → all three buttons stack full-width, primary on top
                  * sm+   → primary + email side-by-side (50/50), Got It on its own line below
                This guarantees the buttons never overflow the modal at any
                viewport from 320px upward, while still feeling balanced on
                larger screens. */}
            <div className="mt-6 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {whatsappHref && (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    data-testid="brochure-unavailable-whatsapp"
                    className="btn-gold inline-flex items-center justify-center gap-2 px-4 py-2.5 text-[10.5px] tracking-[0.18em] uppercase whitespace-nowrap"
                  >
                    <MessageCircle className="h-3.5 w-3.5 shrink-0" /> Chat on WhatsApp
                  </a>
                )}
                {email && (
                  <a
                    href={`mailto:${email}`}
                    data-testid="brochure-unavailable-email"
                    className="btn-ghost inline-flex items-center justify-center gap-2 px-4 py-2.5 text-[10.5px] tracking-[0.18em] uppercase whitespace-nowrap"
                  >
                    <Mail className="h-3.5 w-3.5 shrink-0" /> Email us
                  </a>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="brochure-unavailable-dismiss"
                className="btn-ghost w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-[10px] tracking-[0.22em] uppercase whitespace-nowrap"
              >
                Got it
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const BrochureForm = () => {
  const settings = useSettings();
  const [form, setForm] = useState({
    fullName: "",
    emailAddress: "",
    phoneNumber: "",
    projectOfInterest: "Silva City",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableBrochures, setAvailableBrochures] = useState([]);
  const [unavailableOpen, setUnavailableOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const validate = () => {
    const e = {};
    if (!form.fullName.trim() || form.fullName.trim().length < 2) e.fullName = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailAddress)) e.emailAddress = "Please enter a valid email address.";
    const digits = (form.phoneNumber || "").replace(/\D/g, "");
    if (digits.length < 7) e.phoneNumber = "Please enter a valid phone number.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) => {
    setForm((p) => ({ ...p, [k]: ev.target.value }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      // 1) Capture lead — *always* runs, so the team has the contact even when
      //    no brochures are uploaded yet.
      await submitLead({ ...form, source: "brochure_download" });

      // 2) Fetch the *current* list of brochures. We re-fetch on every submit
      //    instead of relying on the cached SiteContent so that newly-uploaded
      //    PDFs become available without a page reload.
      const list = await fetchBrochures().catch(() => []);
      const active = Array.isArray(list) ? list : [];

      if (active.length > 0) {
        setAvailableBrochures(active);
        // Best-effort auto-trigger of the first PDF so the user gets at least
        // one file immediately. The rest are visible in the on-screen list.
        try {
          await triggerBrochureFileDownload(active[0]);
        } catch {
          // user can still click any tile manually
        }
        toast.success(`Your brochure${active.length === 1 ? " is" : "s are"} ready.`, {
          description:
            active.length === 1
              ? "Saved to your downloads folder."
              : `${active.length} files — see the list below.`,
        });
      } else {
        // No PDFs uploaded yet — show the editable popup.
        setUnavailableOpen(true);
      }

      // Reset form state
      setForm({ fullName: "", emailAddress: "", phoneNumber: "", projectOfInterest: "Silva City" });
      setErrors({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Something went wrong. Please try again.", {
        description: typeof detail === "string" ? detail : "Please verify your details and retry.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleListDownload = async (brochure) => {
    setDownloadingId(brochure.id);
    try {
      await triggerBrochureFileDownload(brochure);
    } finally {
      setDownloadingId(null);
    }
  };

  // Copy resolved from CMS so the admin can edit the message from /admin/settings.
  const popupTitle =
    settings?.brochure?.unavailableTitle || "We'll send it to you shortly.";
  const popupMessage =
    settings?.brochure?.unavailableText ||
    "Thank you for your interest. The brochure isn't available for direct download right now — we'll send it to your inbox shortly, and if you've shared a phone number, our team will reach out via WhatsApp.";
  const whatsappNumber = settings?.contact?.whatsapp;
  const whatsappDigits = settings?.contact?.whatsappDigits;
  const emailAddress = settings?.contact?.email;

  return (
    <>
      <form onSubmit={onSubmit} data-testid="brochure-form" className="glass-card p-6 sm:p-8 backdrop-blur-xl">
        <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
          <span className="gold-divider" /> Download Brochure
        </p>
        <h3 className="font-display text-3xl md:text-4xl mt-4 leading-[1.1]">
          Download Master Layout & Brochure.
        </h3>
        <p className="mt-3 text-white/65 text-sm max-w-xl">
          Share a few details and we&rsquo;ll send the complete Silva City brochure directly to your inbox.
        </p>

        <div className="mt-7 grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] tracking-[0.28em] uppercase text-white/55">Full Name</label>
            <input
              data-testid="brochure-form-name-input"
              type="text"
              value={form.fullName}
              onChange={onChange("fullName")}
              className="input-luxe mt-2 w-full px-4 py-3 text-sm"
              placeholder="e.g. Arjun Mehta"
              autoComplete="name"
            />
            {errors.fullName && <p className="mt-1 text-xs text-red-400">{errors.fullName}</p>}
          </div>
          <div>
            <label className="text-[10px] tracking-[0.28em] uppercase text-white/55">Email</label>
            <input
              data-testid="brochure-form-email-input"
              type="email"
              value={form.emailAddress}
              onChange={onChange("emailAddress")}
              className="input-luxe mt-2 w-full px-4 py-3 text-sm"
              placeholder="arjun@example.com"
              autoComplete="email"
            />
            {errors.emailAddress && <p className="mt-1 text-xs text-red-400">{errors.emailAddress}</p>}
          </div>
          <div>
            <label className="text-[10px] tracking-[0.28em] uppercase text-white/55">Phone</label>
            <input
              data-testid="brochure-form-phone-input"
              type="tel"
              value={form.phoneNumber}
              onChange={onChange("phoneNumber")}
              className="input-luxe mt-2 w-full px-4 py-3 text-sm"
              placeholder="+91 98765 43210"
              autoComplete="tel"
            />
            {errors.phoneNumber && <p className="mt-1 text-xs text-red-400">{errors.phoneNumber}</p>}
          </div>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-xs text-white/45">We respect your privacy. No spam, ever.</p>
          <button
            type="submit"
            disabled={loading}
            data-testid="brochure-form-submit-button"
            className="btn-gold inline-flex items-center justify-center gap-2 px-7 py-3 text-[12px] tracking-[0.22em] uppercase disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Submitting
              </>
            ) : (
              <>
                <Download className="h-4 w-4" /> Download Now
              </>
            )}
          </button>
        </div>

        {availableBrochures.length > 0 && (
          <BrochureDownloadList
            brochures={availableBrochures}
            onDownload={handleListDownload}
            downloadingId={downloadingId}
          />
        )}
      </form>

      <BrochureUnavailableDialog
        open={unavailableOpen}
        onClose={() => setUnavailableOpen(false)}
        title={popupTitle}
        message={popupMessage}
        whatsappNumber={whatsappNumber}
        whatsappDigits={whatsappDigits}
        email={emailAddress}
      />
    </>
  );
};

const ProjectsPage = () => {
  const seo = getPageSeo("projects");
  // Page-specific JSON-LD for Silva City.
  //
  // We deliberately do NOT use the `Product` schema here — Google requires
  // `offers`/`review`/`aggregateRating` on Product items, and a master-plan
  // township doesn't fit that model cleanly (it's a real-estate listing, not
  // a SKU with a fixed price). We use the more accurate combination:
  //   * `RealEstateListing` (https://schema.org/RealEstateListing) — Google's
  //     recommended type for property listings; no required Product fields.
  //   * `Place` — gives the geo + address.
  //   * `ResidentialComplex` (Google-recognized) — clarifies it's a township.
  const projectJsonLd = {
    "@context": "https://schema.org",
    "@type": ["RealEstateListing", "Place", "ResidentialComplex"],
    "@id": "https://www.seetaiproperties.com/projects#silva-city",
    name: "Silva City — 66-Acre Premium NA Plotted Township",
    alternateName: "Silva City Township, Bhandgaon",
    description:
      "Silva City is a Town Planning approved 66-acre premium NA plotted township at Khor, Bhandgaon — at the convergence of Pune's next-generation infrastructure corridor. Plots from 1,200 to 5,000 sq. ft., positioned as a long-term wealth-creation asset with steady appreciation potential.",
    url: "https://www.seetaiproperties.com/projects",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Airport Link Road, Jejuri Road, Bhandgaon",
      addressLocality: "Bhandgaon",
      addressRegion: "Maharashtra",
      postalCode: "412214",
      addressCountry: "IN",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 18.4125,
      longitude: 74.325306,
    },
    numberOfRooms: { "@type": "QuantitativeValue", unitText: "ACRE", value: 66 },
    amenityFeature: [
      { "@type": "LocationFeatureSpecification", name: "Town Planning Approved", value: true },
      { "@type": "LocationFeatureSpecification", name: "NA Plotted Layout", value: true },
      { "@type": "LocationFeatureSpecification", name: "Master Plan Township", value: true },
      { "@type": "LocationFeatureSpecification", name: "Gated Community", value: true },
    ],
    publicAccess: false,
    isAccessibleForFree: false,
    image: [
      "https://www.seetaiproperties.com/og-image.jpg",
    ],
    brand: {
      "@type": "Organization",
      name: "Seetai Properties",
      url: "https://www.seetaiproperties.com",
    },
    provider: {
      "@type": "RealEstateAgent",
      name: "Seetai Properties Private Limited",
      url: "https://www.seetaiproperties.com",
      areaServed: { "@type": "State", name: "Maharashtra, India" },
    },
  };
  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        jsonLd={projectJsonLd}
      />
      {/* HERO */}
      <section data-testid="projects-hero" className="relative h-[78vh] min-h-[560px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <DynamicImage
            mediaKey="hero_projects"
            alt="Aerial drone view of a luxurious gated township at dusk"
            ratio=""
            className="h-full w-full"
            imgClassName="h-full w-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="container-narrow relative z-10 pb-16">
          <motion.p {...fadeUp} className="text-[11px] tracking-[0.36em] uppercase text-[color:var(--sp-gold)]">
            <span className="gold-divider" /> Our Flagship Project
          </motion.p>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="font-display text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.05] max-w-5xl">
            Silva City — <span className="italic text-white/85">Smart Investment</span> for the Future.
          </motion.h1>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="mt-7 flex flex-wrap gap-3 text-[11px] tracking-[0.22em] uppercase text-white/75">
            <span className="rounded-full border border-[color:rgba(212,175,55,0.45)] bg-black/40 backdrop-blur-md px-4 py-2 text-[color:var(--sp-gold)]">TP Approved</span>
            <span className="rounded-full border border-white/15 bg-black/40 backdrop-blur-md px-4 py-2">Khor, Bhandgaon</span>
            <span className="rounded-full border border-white/15 bg-black/40 backdrop-blur-md px-4 py-2">1,200 – 5,000 sq.ft.</span>
          </motion.div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section data-testid="projects-overview" className="luxe-section">
        <div className="container-narrow grid lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> Project Overview
            </p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.1]">
              A premium plotted township, masterfully composed.
            </h2>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-7 space-y-5 text-white/75 text-base md:text-lg leading-relaxed">
            <p>
              <span className="text-[color:var(--sp-gold)] font-medium">Town Planning approved Premium NA Plots</span>
              {" "}ranging from 1,200 to 5,000 sq. ft., located in Khor, Bhandgaon — strategically positioned at the
              convergence of Pune’s next-gen infrastructure corridor.
            </p>
            <p>
              Silva City is designed as a fully integrated, low-density township where curated
              architecture meets thoughtful land planning. Every plot inherits wide roads,
              utilities-on-site, dedicated power back-up, and direct access to forest trails,
              wellness amenities, and the heritage clubhouse.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 pt-3">
              <div className="glass-card p-5">
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Plot Sizes</p>
                <p className="mt-2 font-display text-sm sm:text-base xl:text-lg text-white leading-tight tracking-tight whitespace-nowrap">{"1,200\u20135,000 sq.ft."}</p>
              </div>
              <div className="glass-card p-5">
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Approval</p>
                <p className="mt-2 font-display text-sm sm:text-base xl:text-lg text-white leading-tight tracking-tight whitespace-nowrap">{"TP \u2022 NA Order"}</p>
              </div>
              <div className="glass-card p-5">
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">Location</p>
                <p className="mt-2 font-display text-sm sm:text-base xl:text-lg text-white leading-tight tracking-tight whitespace-nowrap">Khor, Bhandgaon</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTERACTIVE 3D TOWNSHIP MAP */}
      <section data-testid="projects-3d-map" className="luxe-section bg-[#0a0a0a] border-y border-white/5">
        <div className="container-narrow">
          <motion.div {...fadeUp} className="max-w-3xl mb-10">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> Interactive 3D Township
            </p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.1]">
              Step inside <span className="italic text-white/85">Silva City</span> — in three dimensions.
            </h2>
            <p className="mt-4 text-white/65 text-base md:text-lg leading-relaxed">
              Drag to rotate the 66-acre township, scroll to zoom, and tap any sector,
              park, or the entrance gate to explore amenity-grade detail with
              images and voiceovers.
            </p>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }}>
            <ProjectMap3D />
          </motion.div>
        </div>
      </section>

      {/* STRATEGIC LOCATION TIMELINE */}
      <section data-testid="projects-location" className="luxe-section bg-[#0a0a0a] border-y border-white/5">
        <div className="container-narrow">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> Strategic Location
            </p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.1]">
              At the centre of Pune’s <span className="italic text-white/85">next decade.</span>
            </h2>
          </motion.div>

          <div className="relative mt-16">
            {/* vertical line (md+) */}
            <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[color:rgba(212,175,55,0.5)] via-[color:rgba(212,175,55,0.2)] to-transparent" />
            {/* mobile vertical */}
            <div className="md:hidden absolute left-3 top-0 bottom-0 w-px bg-[color:rgba(212,175,55,0.3)]" />

            <div className="space-y-12">
              {LOCATION_POINTS.map((p, i) => {
                const Icon = p.icon;
                const isLeft = i % 2 === 0;
                return (
                  <motion.div
                    key={p.title}
                    {...fadeUp}
                    transition={{ ...fadeUp.transition, delay: 0.08 * i }}
                    className={`relative grid md:grid-cols-2 gap-6 md:gap-12 items-center pl-10 md:pl-0`}
                  >
                    {/* node */}
                    <div className="absolute md:left-1/2 left-3 md:-translate-x-1/2 -translate-x-1/2 flex h-5 w-5 items-center justify-center">
                      <span className={`h-3 w-3 rounded-full ${p.accent === "emerald" ? "bg-[color:var(--sp-emerald)]" : "bg-[color:var(--sp-gold)]"} shadow-[0_0_0_6px_rgba(212,175,55,0.10)]`} />
                    </div>

                    <div className={`md:col-span-1 ${isLeft ? "md:text-right md:pr-8" : "md:order-2 md:pl-8"}`}>
                      <div className={`inline-flex items-center gap-2 rounded-full border border-white/12 px-4 py-1.5 text-[11px] tracking-[0.22em] uppercase text-white/75`}>
                        <Icon className={`h-3.5 w-3.5 ${p.accent === "emerald" ? "text-[color:var(--sp-emerald)]" : "text-[color:var(--sp-gold)]"}`} />
                        {p.distance}
                      </div>
                      <h3 className="font-display text-2xl md:text-3xl mt-4">{p.title}</h3>
                      <p className="mt-2 text-white/65 text-[15px] leading-relaxed max-w-md md:inline-block">{p.desc}</p>
                    </div>
                    {/* spacer on opposite side for desktop */}
                    <div className="hidden md:block" />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* AMENITIES GRID */}
      <section data-testid="projects-amenities" className="luxe-section">
        <div className="container-narrow">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> World-Class Amenities
            </p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.1]">
              Six experiences — designed to anchor a lifetime.
            </h2>
          </motion.div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {AMENITIES.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.05 * i }}
                className="glass-card p-7 card-lift"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.45)] bg-[color:var(--sp-gold-soft)]">
                  <Icon className="h-5 w-5 text-[color:var(--sp-gold)]" />
                </div>
                <h3 className="font-display text-2xl mt-5">{title}</h3>
                <p className="mt-3 text-white/65 leading-relaxed text-[15px]">{desc}</p>
                <div className="mt-6 inline-flex items-center text-[11px] tracking-[0.22em] uppercase text-white/40">
                  Amenity 0{i + 1}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="py-10 bg-[#0a0a0a]">
        <div className="container-narrow grid grid-cols-2 md:grid-cols-4 gap-3">
          <DynamicImage mediaKey="gallery_1" alt="Silva City gallery 1" className="rounded-xl border border-white/10 neon-border" />
          <DynamicImage mediaKey="gallery_2" alt="Silva City gallery 2" className="rounded-xl border border-white/10 neon-border" />
          <DynamicImage mediaKey="gallery_3" alt="Silva City gallery 3" className="rounded-xl border border-white/10 neon-border" />
          <DynamicImage mediaKey="gallery_4" alt="Silva City gallery 4" className="rounded-xl border border-white/10 neon-border" />
        </div>
      </section>

      {/* BROCHURE FORM */}
      <section data-testid="projects-brochure" className="luxe-section">
        <div className="container-narrow">
          <BrochureForm />
        </div>
      </section>
    </>
  );
};

export default ProjectsPage;
